import { EMPTY, debounceTime, filter, map, of, switchMap } from 'rxjs';
import { inject } from '@angular/core';
import { AppStore } from '@navstation-portal/reporting/data-access/app';
import { FormState } from '@navstation-portal/reporting/data-model/vessel-report';
import { mapResponse } from '@ngrx/operators';
import { signalStoreFeature } from '@ngrx/signals';
import { Events, withEventHandlers } from '@ngrx/signals/events';
import { REPORT_API_SERVICE } from '../report-dependencies';
import { getFieldValueFromFormState } from '../vm-builder-helpers';
import { operationalCalculationEvents, operationalEvents, operationalReferenceDataEvents } from './operational-events';

const DISPLACEMENT_TRIGGER_FIELDS = ['draughtForward', 'draughtAft'];
const DISPLACEMENT_DEBOUNCE_MS = 2000;

interface OperationalStoreState {
  formState: () => FormState;
}

export function withOperationalEffects() {
  return signalStoreFeature(
    withEventHandlers((rawStore) => {
      const store = rawStore as unknown as OperationalStoreState;
      const events = inject(Events);
      const reportService = inject(REPORT_API_SERVICE);
      const appStore = inject(AppStore);

      const getCurrentValue = (fieldId: string) =>
        getFieldValueFromFormState(store.formState(), 'operatingConditions', fieldId) as number | undefined;

      // draught averages forward/aft; skip (rather than clear a prior value) until both are known
      const requestDisplacement = (draughtForward: number | undefined, draughtAft: number | undefined) => {
        if (draughtForward == null || draughtAft == null) return undefined;
        return operationalCalculationEvents.getDisplacement({ draught: (draughtForward + draughtAft) / 2 });
      };

      return {
        recalculateDisplacementOnValueChange$: events.on(operationalEvents.updateFormStateValue).pipe(
          filter(
            ({ payload }) =>
              payload.sectionId === 'operatingConditions' && DISPLACEMENT_TRIGGER_FIELDS.includes(payload.fieldId)
          ),
          debounceTime(DISPLACEMENT_DEBOUNCE_MS),
          switchMap(() => {
            const event = requestDisplacement(getCurrentValue('draughtForward'), getCurrentValue('draughtAft'));
            return event ? of(event) : EMPTY;
          })
        ),
        recalculateDisplacementOnInit$: events.on(operationalEvents.initialiseFormState).pipe(
          switchMap(({ payload: { initialData } }) => {
            const operatingConditions = (initialData as { operatingConditions?: Record<string, unknown> } | undefined)
              ?.operatingConditions;
            if (!operatingConditions) return EMPTY;

            const event = requestDisplacement(
              operatingConditions['draughtForward'] as number | undefined,
              operatingConditions['draughtAft'] as number | undefined
            );
            return event ? of(event) : EMPTY;
          })
        ),
        getDisplacement$: events.on(operationalCalculationEvents.getDisplacement).pipe(
          switchMap(({ payload: { draught } }) => {
            const vesselId = appStore.selectedVesselId();
            if (!vesselId) return EMPTY;

            return reportService.getDisplacement(vesselId, draught).pipe(
              mapResponse({
                next: (response) => operationalCalculationEvents.getDisplacementSuccess(response),
                error: (err: Error) => {
                  console.error('getDisplacement error:', err);
                  return operationalCalculationEvents.getDisplacementFailed(err);
                },
              })
            );
          })
        ),
        applyDisplacementOnSuccess$: events.on(operationalCalculationEvents.getDisplacementSuccess).pipe(
          map(({ payload: { displacement } }) => {
            return operationalEvents.updateFormStateValue({
              sectionId: 'stabilityConditions',
              fieldId: 'displacement',
              value: displacement,
            });
          })
        ),
        getPortCallPurposesOnInit$: events
          .on(operationalEvents.initialiseFormState)
          .pipe(map(() => operationalReferenceDataEvents.getPortCallPurposes())),
        getPortCallPurposes$: events.on(operationalReferenceDataEvents.getPortCallPurposes).pipe(
          switchMap(() =>
            reportService.getPortCallPurposes().pipe(
              mapResponse({
                next: (purposes) => operationalReferenceDataEvents.getPortCallPurposesSuccess(purposes),
                error: (err: Error) => {
                  console.error('getPortCallPurposes error:', err);
                  return operationalReferenceDataEvents.getPortCallPurposesFailed(err);
                },
              })
            )
          )
        ),
      };
    })
  );
}
